/**
 * Die Bewegung, mit der etwas **ankommt** statt einfach dazustehen: einmal
 * weich von unten herauf und eingeblendet — und rückwärts wieder hinaus.
 *
 * Gebraucht überall dort, wo ein ganzer Zustand die Fläche übernimmt: der leere
 * Zustand eines Projekts oder von Ruhend, und absehbar derselbe bei
 * Gewohnheiten. Deshalb in `lib/` und nicht im Feature — Features importieren
 * einander nie, `lib/` ist der gemeinsame Boden.
 *
 * **Unterschied zu `growMotion`:** Dort macht ein Element in einer Reihe Platz,
 * die Nachbarn rücken. Hier bewegt sich etwas, das seinen Platz schon hat — es
 * gibt nichts zu staffeln, nur eine Strecke und eine Kurve.
 *
 * **Die Verzögerung ist kein Zierrat.** Sie macht die Bewegung dort überhaupt
 * sichtbar, wo gleichzeitig etwas Größeres läuft: Beim Schließen des
 * Projekt-Canvas fährt die ganze Listenspalte über die halbe Fensterbreite
 * herein (0,25 s), und der leere Zustand erscheint im ersten Frame davon. Acht
 * Pixel nach oben sind darin nicht wahrnehmbar. Der Default liegt deshalb
 * knapp hinter dieser Bewegung.
 *
 * Bewusst ohne DOM-Bezug: Hier wird gerechnet, in der Komponente eingesetzt,
 * und geprüft ohne gerendertes Bauteil.
 */

import type { TargetAndTransition } from 'framer-motion'

export interface ArriveMotionOptions {
  /**
   * Strecke in Pixeln, aus der heraus angekommen wird — von unten nach oben.
   * Default 8: weit genug, um als Bewegung zu lesen, kurz genug, um nicht als
   * Sprung zu wirken.
   */
  distance?: number
  /** Dauer der Ankunft. Default 0,32 s. */
  duration?: number
  /**
   * Verzug vor der Ankunft. Default 0,26 s — zehn Millisekunden hinter der
   * 0,25-s-Bewegung des Projekt-Canvas. Wer keine große Bewegung neben sich
   * hat, setzt 0.
   */
  delay?: number
  /**
   * Dauer des Abgangs. Default 0,2 s: Verschwinden darf schneller sein als
   * Ankommen, sonst wartet man auf etwas, das man schon nicht mehr will.
   */
  exitDuration?: number
  /**
   * Herauf mit kleinem Überschwingen: Der Weg federt über das Ziel hinaus und
   * zurück, die Deckkraft ist schon nach 0,2 s voll da — sonst sähe man das
   * Überschwingen halb durchsichtig. So taucht die Listen-Werkzeug-Pille nach
   * dem Anlegen wieder auf (Prototyp `tools/list-toolbar-retract-prototype`,
   * Variante D, 01.10.2026). Der Abgang bleibt, wie er ist.
   */
  overshoot?: boolean
  /**
   * Startgröße (Vorgabe 1 = keine). Mit einem Wert unter 1 wächst die Fläche beim Ankommen leicht
   * heran — so geht die **Suche** auf. Setzt `compositor` voraus.
   */
  scale?: number
  /**
   * Als ganzer `transform` statt als Einzelwert `y` (Vorgabe `false`): framer-motion gibt ihn an
   * die Web Animations API, und er läuft im Compositor weiter, während der Main-Thread lädt und
   * rendert (siehe `revealSequence`). Für Flächen, die zugleich mit ihrem Inhalt aufgehen.
   * Ohne Überschwingen — die Kurven je Wert gelten nur für `y`.
   */
  compositor?: boolean
  /**
   * Schaltet die Bewegung ab — für `prefers-reduced-motion`. Der Zustand ist
   * dann sofort da und ohne Weg. Wird als Wert übergeben und nicht hier
   * abgefragt, damit die Funktion rein bleibt; in React liefert ihn
   * `useReducedMotion()`.
   */
  reduced?: boolean
}

/** Was eine `motion`-Komponente braucht: drei Zustände. */
export interface ArriveMotion {
  initial: TargetAndTransition
  animate: TargetAndTransition
  exit: TargetAndTransition
}

/**
 * Herein weich auslaufend, hinaus weich anlaufend — dieselbe Kurve gespiegelt.
 * Das Paar führt die App schon beim Hereinfahren der Navigation
 * (`nav-peek-in` / `nav-peek-out`, MainLayout.scss).
 */
const EASE_IN = [0.22, 1, 0.36, 1] as const
const EASE_OUT = [0.64, 0, 0.78, 0] as const
/** Der zweite Kontrollpunkt über 1 trägt die Kurve über ihr Ziel hinaus. */
const EASE_OVERSHOOT = [0.34, 1.45, 0.64, 1] as const
const OVERSHOOT_FADE = 0.2

/**
 * @example
 * const arrive = arriveMotion({ reduced: useReducedMotion() ?? false })
 * <motion.div initial={arrive.initial} animate={arrive.animate} exit={arrive.exit} />
 *
 * @example
 * // Ohne große Nachbarbewegung darf sie sofort loslegen:
 * arriveMotion({ delay: 0 })
 */
export function arriveMotion({
  distance = 8,
  duration = 0.32,
  delay = 0.26,
  exitDuration = 0.2,
  overshoot = false,
  scale = 1,
  compositor = false,
  reduced = false,
}: ArriveMotionOptions = {}): ArriveMotion {
  if (compositor) {
    // Beide Enden mit denselben Funktionen, sonst lässt sich der `transform` nicht interpolieren.
    const away = `translateY(${distance}px) scale(${scale})`
    const home = 'translateY(0px) scale(1)'
    if (reduced) {
      return {
        initial: { opacity: 1, transform: home },
        animate: { opacity: 1, transform: home, transition: { duration: 0 } },
        exit: { opacity: 0, transition: { duration: 0 } },
      }
    }
    return {
      initial: { opacity: 0, transform: away },
      animate: { opacity: 1, transform: home, transition: { duration, delay, ease: EASE_IN } },
      exit: { opacity: 0, transform: away, transition: { duration: exitDuration, ease: EASE_OUT } },
    }
  }

  // Kein Weg und keine Zeit: Der Zustand ist da, fertig. Die drei Objekte
  // bleiben trotzdem bestehen, damit die Aufrufstelle nicht verzweigen muss.
  if (reduced) {
    const still = { opacity: 1, y: 0, transition: { duration: 0 } }
    return { initial: { opacity: 1, y: 0 }, animate: still, exit: { opacity: 0, transition: { duration: 0 } } }
  }

  // Je Wert eine eigene Übergabe — framer erbt den gemeinsamen Verzug nicht in
  // sie hinein, er steht deshalb in beiden.
  const arrive = overshoot
    ? {
      y: { duration, delay, ease: EASE_OVERSHOOT },
      opacity: { duration: Math.min(OVERSHOOT_FADE, duration), delay, ease: EASE_IN },
    }
    : { duration, delay, ease: EASE_IN }

  return {
    initial: { opacity: 0, y: distance },
    animate: { opacity: 1, y: 0, transition: arrive },
    // Kein Verzug auf dem Rückweg: Der Verzug gehört der Ankunft, weil sie auf
    // eine fremde Bewegung wartet. Beim Gehen wartet niemand.
    exit: { opacity: 0, y: distance, transition: { duration: exitDuration, ease: EASE_OUT } },
  }
}
